import { useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { AuthProvider, useAuth } from "./contexts/AuthContext"
import { ThemeProvider, useTheme } from "./contexts/ThemeContext"
import AdminPage from "./components/AdminPage"
import AuthModal from "./components/AuthModal"
import BottomNav from "./components/BottomNav"
import ChangePasswordModal from "./components/ChangePasswordModal"
import HelpModal from "./components/HelpModal"
import HomePage from "./components/HomePage"
import Logo from "./components/Logo"
import Map from "./components/Map"
import SpotDetailSheet from "./components/SpotDetail"
import SpotForm from "./components/SpotForm"
import TermsModal from "./components/TermsModal"
import type { Spot, SpotDetail as SpotDetailType } from "./types"

const API_URL = import.meta.env.VITE_API_URL || "/api"

function AppContent() {
  const { t, i18n } = useTranslation()
  const { auth, logout, refreshUser } = useAuth()
  const { isDark, toggle } = useTheme()
  const [activeTab, setActiveTab] = useState<"home" | "map" | "favorites" | "profile" | "admin">("home")
  const [addingSpot, setAddingSpot] = useState(false)
  const [showAuth, setShowAuth] = useState(false)
  const [selectedSpot, setSelectedSpot] = useState<SpotDetailType | null>(null)
  const [newSpotCoords, setNewSpotCoords] = useState<{ lat: number; lng: number } | null>(null)
  const [spots, setSpots] = useState<Spot[]>([])
  const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number } | null>(null)
  const [showHelp, setShowHelp] = useState(false)
  const [showTerms, setShowTerms] = useState(false)
  const [resendLoading, setResendLoading] = useState(false)
  const [resendMessage, setResendMessage] = useState<string | null>(null)
  const [isPlacingSpot, setIsPlacingSpot] = useState(false)
  const [pendingCoords, setPendingCoords] = useState<{ lat: number; lng: number } | null>(null)
  const [showChangePassword, setShowChangePassword] = useState(false)
  const [deletePassword, setDeletePassword] = useState("")
  const [transferUsername, setTransferUsername] = useState("")
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [refreshKey, setRefreshKey] = useState(0)

  useEffect(() => {
    const headers: Record<string, string> = {}
    if (auth.token) headers["Authorization"] = `Bearer ${auth.token}`
    fetch(`${API_URL}/spots`, { headers })
      .then((res) => res.json())
      .then((data) => setSpots(data.items))
      .catch(() => {})
  }, [auth.token])

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const token = params.get("token")
    if (token && window.location.pathname === "/verify") {
      fetch(`${API_URL}/auth/verify/${token}`)
        .then((res) => {
          if (res.ok) {
            refreshUser()
            window.history.replaceState({}, "", "/")
            alert(t("auth.verifySuccess"))
          } else {
            window.history.replaceState({}, "", "/")
            alert(t("auth.verifyFailed"))
          }
        })
        .catch(() => {
          window.history.replaceState({}, "", "/")
          alert(t("auth.verifyFailed"))
        })
    }
  }, [])

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const spotId = params.get("spot")
    if (spotId) {
      const headers: Record<string, string> = {}
      if (auth.token) headers["Authorization"] = `Bearer ${auth.token}`
      fetch(`${API_URL}/spots/${spotId}`, { headers })
        .then((res) => {
          if (res.ok) return res.json()
          throw new Error("Spot not found")
        })
        .then((detail: SpotDetailType) => {
          setSelectedSpot(detail)
          setActiveTab("map")
          setMapCenter({ lat: detail.latitude, lng: detail.longitude })
          window.history.replaceState({}, "", "/")
        })
        .catch(() => {
          window.history.replaceState({}, "", "/")
        })
    }
  }, [auth.token])

  const handleMapClick = (lat: number, lng: number) => {
    if (!isPlacingSpot) return
    if (!auth.isAuthenticated) {
      setShowAuth(true)
      return
    }
    setPendingCoords({ lat, lng })
  }

  const confirmSpotLocation = () => {
    if (!pendingCoords) return
    setNewSpotCoords(pendingCoords)
    setAddingSpot(true)
    setPendingCoords(null)
    setIsPlacingSpot(false)
  }

  const cancelSpotLocation = () => {
    setPendingCoords(null)
  }

  const handleMarkerClick = useCallback(async (spot: Spot) => {
    try {
      const headers: Record<string, string> = {}
      if (auth.token) headers["Authorization"] = `Bearer ${auth.token}`
      const res = await fetch(`${API_URL}/spots/${spot.id}`, { headers })
      if (res.ok) {
        const detail: SpotDetailType = await res.json()
        setSelectedSpot(detail)
      }
    } catch {}
  }, [auth.token])

  const handleSpotSaved = (spot: Spot) => {
    setSpots((prev) => [...prev, spot])
    setAddingSpot(false)
    setNewSpotCoords(null)
    setIsPlacingSpot(false)
  }

  const handleSpotUpdated = useCallback((updated: SpotDetailType) => {
    setSelectedSpot(updated)
    setSpots((prev) =>
      prev.map((s) =>
        s.id === updated.id
          ? {
              ...s,
              name: updated.name,
              description: updated.description,
              category: updated.category,
              rating_avg: updated.rating_avg,
              rating_count: updated.rating_count,
            }
          : s
      )
    )
  }, [])

  const handleSpotDeleted = useCallback(async (spotId: string) => {
    if (!auth.token) return
    try {
      const res = await fetch(`${API_URL}/spots/${spotId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${auth.token}` },
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.detail || "Failed to delete spot")
      }
      setSpots((prev) => prev.filter((s) => s.id !== spotId))
      setSelectedSpot(null)
      setRefreshKey((k) => k + 1)
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to delete spot")
    }
  }, [auth.token])

  const handleSearch = (lat: number, lng: number) => {
    setMapCenter({ lat, lng })
    setActiveTab("map")
  }

  const handleChangePassword = async (current: string, newPass: string) => {
    if (!auth.token) throw new Error("Not authenticated")
    const res = await fetch(`${API_URL}/auth/change-password`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${auth.token}`,
      },
      body: JSON.stringify({ current_password: current, new_password: newPass }),
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      throw new Error(err.detail || "Failed to change password")
    }
  }

  const handleDeleteAccount = async () => {
    if (!auth.token) return
    setDeleteLoading(true)
    setDeleteError(null)
    try {
      const res = await fetch(`${API_URL}/auth/delete-account`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${auth.token}`,
        },
        body: JSON.stringify({
          current_password: deletePassword,
          transfer_to_username: transferUsername.trim() || null,
        }),
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.detail || "Failed to delete account")
      }
      logout()
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : t("error.generic"))
    } finally {
      setDeleteLoading(false)
    }
  }

  const handleAddSpotClick = () => {
    if (!auth.isAuthenticated) {
      setShowAuth(true)
      return
    }
    setIsPlacingSpot(true)
    setActiveTab("map")
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setMapCenter({ lat: pos.coords.latitude, lng: pos.coords.longitude })
        },
        () => {}
      )
    }
  }

  const renderContent = () => {
    if (activeTab === "home") {
      return (
        <HomePage
          onExplore={() => setActiveTab("map")}
          onLogin={() => setShowAuth(true)}
          onTerms={() => setShowTerms(true)}
        />
      )
    }

    if (activeTab === "favorites") {
      if (!auth.isAuthenticated) {
        return (
          <div className="h-full flex flex-col items-center justify-center p-8 text-center">
            <p className="text-gray-500 dark:text-gray-400 mb-4">{t("favorites.loginRequired")}</p>
            <button
              onClick={() => setShowAuth(true)}
              className="px-6 py-3 rounded-xl bg-steel text-white font-medium"
            >
              {t("auth.login")}
            </button>
          </div>
        )
      }
      return (
        <div className="h-full overflow-y-auto p-4 pb-24">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
            {t("nav.favorites")}
          </h2>
          <FavoriteSpots key={`fav-${refreshKey}`} onSpotClick={handleMarkerClick} />
        </div>
      )
    }

    if (activeTab === "profile") {
      return (
        <div className="h-full overflow-y-auto p-4 pb-24">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
            {t("nav.profile")}
          </h2>
          {auth.isAuthenticated ? (
            <div className="space-y-4">
              <div className="bg-white dark:bg-deepNavy-light rounded-xl p-4 shadow">
                <div className="w-16 h-16 rounded-full bg-steel text-white flex items-center justify-center text-2xl font-bold mb-3">
                  {(auth.user?.username || "U").charAt(0).toUpperCase()}
                </div>
                <p className="font-semibold text-gray-900 dark:text-white text-lg">
                  {auth.user?.username}
                </p>
                <p className="text-gray-500 dark:text-gray-400 text-sm">{auth.user?.email}</p>
                {auth.user?.email_verified ? (
                  <span className="inline-flex items-center gap-1 mt-2 text-xs text-forest dark:text-forest-light font-medium">
                    <span>✓</span> {t("auth.verified")}
                  </span>
                ) : (
                  <div className="mt-3">
                    <p className="text-xs text-amber-600 dark:text-amber-400 mb-2">{t("auth.notVerified")}</p>
                    <button
                      onClick={async () => {
                        setResendLoading(true)
                        setResendMessage(null)
                        try {
                          const res = await fetch(`${API_URL}/auth/resend-verification`, {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({ email: auth.user?.email }),
                          })
                          if (res.ok) {
                            setResendMessage(t("auth.resendSuccess"))
                          } else {
                            setResendMessage(t("auth.resendFailed"))
                          }
                        } catch {
                          setResendMessage(t("auth.resendFailed"))
                        } finally {
                          setResendLoading(false)
                        }
                      }}
                      disabled={resendLoading}
                      className="text-xs px-3 py-1.5 rounded-lg bg-steel/10 dark:bg-steel/20 text-steel-dark dark:text-steel-light font-medium disabled:opacity-50"
                    >
                      {resendLoading ? t("auth.resending") : t("auth.resendVerification")}
                    </button>
                    {resendMessage && (
                      <p className="text-xs mt-1 text-gray-500 dark:text-gray-400">{resendMessage}</p>
                    )}
                  </div>
                )}
              </div>
              <MySpots key={`my-${refreshKey}`} onSpotClick={handleMarkerClick} />
              {auth.user?.is_admin && (
                <button
                  onClick={() => setActiveTab("admin")}
                  className="w-full py-3 rounded-xl bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 font-medium"
                >
                  {t("admin.title")}
                </button>
              )}
              <button
                onClick={() => setShowChangePassword(true)}
                className="w-full py-3 rounded-xl bg-slate-100 dark:bg-deepNavy-light text-slate-700 dark:text-slate-300 font-medium"
              >
                {t("auth.changePassword")}
              </button>
              <button
                onClick={() => setShowTerms(true)}
                className="w-full py-3 rounded-xl bg-slate-100 dark:bg-deepNavy-light text-slate-700 dark:text-slate-300 font-medium"
              >
                {t("terms.title")}
              </button>
              <button
                onClick={logout}
                className="w-full py-3 rounded-xl bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 font-medium"
              >
                {t("auth.logout")}
              </button>
              <div className="border-t border-gray-200 dark:border-slate-700 pt-4 mt-2">
                <h3 className="text-sm font-semibold text-red-600 dark:text-red-400 mb-3">
                  {t("auth.deleteAccount")}
                </h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                      {t("auth.currentPassword")}
                    </label>
                    <input
                      type="password"
                      value={deletePassword}
                      onChange={(e) => setDeletePassword(e.target.value)}
                      className="w-full px-3 py-2 rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-red-400"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                      {t("auth.transferSpotsTo")} ({t("auth.optional")})
                    </label>
                    <input
                      type="text"
                      value={transferUsername}
                      onChange={(e) => setTransferUsername(e.target.value)}
                      placeholder={t("auth.transferUsernamePlaceholder")}
                      className="w-full px-3 py-2 rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-red-400"
                    />
                  </div>
                  {deleteError && (
                    <p className="text-sm text-red-600 dark:text-red-400">{deleteError}</p>
                  )}
                  <button
                    onClick={() => {
                      if (confirm(t("auth.confirmDeleteAccount"))) {
                        handleDeleteAccount()
                      }
                    }}
                    disabled={deleteLoading || !deletePassword}
                    className="w-full py-2.5 rounded-xl bg-red-600 text-white font-medium text-sm disabled:opacity-50"
                  >
                    {deleteLoading ? t("loading") : t("auth.deleteAccount")}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center">
              <p className="text-gray-500 dark:text-gray-400 mb-4">{t("profile.notLoggedIn")}</p>
              <button
                onClick={() => setShowAuth(true)}
                className="px-6 py-3 rounded-xl bg-steel text-white font-medium"
              >
                {t("auth.login")}
              </button>
            </div>
          )}
          <div className="mt-6">
            <button
              onClick={toggle}
              className="w-full py-3 rounded-xl bg-slate-100 dark:bg-deepNavy-light text-slate-700 dark:text-slate-300 font-medium flex items-center justify-center gap-2"
            >
              {isDark ? "☀️" : "🌙"} {isDark ? t("theme.light") : t("theme.dark")}
            </button>
          </div>
        </div>
      )
    }

    if (activeTab === "admin") {
      return <AdminPage />
    }

    return (
      <>
        <Map
          onMapClick={handleMapClick}
          onMarkerClick={handleMarkerClick}
          onSearch={handleSearch}
          onConfirmLocation={confirmSpotLocation}
          onCancelLocation={cancelSpotLocation}
          spots={spots}
          center={mapCenter}
          isPlacingSpot={isPlacingSpot}
          pendingCoords={pendingCoords}
          confirmLabels={{
            title: t("map.confirmLocation"),
            yes: t("map.confirmYes"),
            no: t("map.confirmNo"),
          }}
        />
        {isPlacingSpot && !pendingCoords && (
          <div className="absolute top-16 left-1/2 -translate-x-1/2 z-20 bg-steel text-white px-4 py-2 rounded-xl shadow-lg text-sm font-medium animate-fade-in-up">
            {t("map.tapToPlace")}
          </div>
        )}
      </>
    )
  }

  return (
    <div className="h-screen w-screen flex flex-col bg-white dark:bg-slate-950 text-gray-900 dark:text-white">
      <header className="bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 dark:from-slate-800 dark:via-slate-900 dark:to-slate-800 backdrop-blur-md border-b border-gray-300/50 dark:border-slate-700/50 text-gray-900 dark:text-white px-4 py-3 flex items-center justify-between z-10 shrink-0">
        <div className="flex items-center gap-3">
          <Logo className="w-28 h-28" />
          <p className="font-['Outfit'] text-xs sm:text-sm md:text-base lg:text-lg font-medium text-gray-700 dark:text-gray-200 tracking-wide">{t("app.tagline")}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowHelp(true)}
            className="text-lg p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-white/15 transition-colors active:scale-90"
            title={t("nav.help")}
          >
            ❓
          </button>
          <button
            onClick={toggle}
            className="text-lg p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-white/15 transition-colors active:scale-90"
          >
            {isDark ? "☀️" : "🌙"}
          </button>
          <select
            value={i18n.resolvedLanguage || "en"}
            onChange={(e) => i18n.changeLanguage(e.target.value)}
            className="bg-gray-100 dark:bg-black/20 text-gray-900 dark:text-white text-xs px-2 py-1.5 rounded-lg border border-gray-300 dark:border-white/30 backdrop-blur-sm cursor-pointer hover:bg-gray-200 dark:hover:bg-white/30 transition-colors"
          >
            <option value="en" className="text-gray-900">EN</option>
            <option value="es" className="text-gray-900">ES</option>
          </select>
        </div>
      </header>

      <main className="flex-1 relative overflow-hidden">
        {renderContent()}

        {showAuth && <AuthModal onClose={() => setShowAuth(false)} />}
        {showHelp && <HelpModal onClose={() => setShowHelp(false)} />}
        {showTerms && <TermsModal onClose={() => setShowTerms(false)} />}
        {showChangePassword && (
          <ChangePasswordModal
            onClose={() => setShowChangePassword(false)}
            onSubmit={handleChangePassword}
          />
        )}

        {selectedSpot && (
          <SpotDetailSheet
            spot={selectedSpot}
            onClose={() => setSelectedSpot(null)}
            onSpotUpdated={handleSpotUpdated}
            onDelete={handleSpotDeleted}
          />
        )}

        {addingSpot && (
          <SpotForm
            latitude={newSpotCoords?.lat}
            longitude={newSpotCoords?.lng}
            onSaved={handleSpotSaved}
            onCancel={() => {
              setAddingSpot(false)
              setNewSpotCoords(null)
              setIsPlacingSpot(false)
            }}
          />
        )}
      </main>

      <BottomNav
        activeTab={activeTab}
        onTabChange={(tab) => {
          if (tab === "home" || tab === "map" || tab === "favorites" || tab === "profile") {
            if (tab === "map" && isPlacingSpot) {
              setIsPlacingSpot(false)
              setPendingCoords(null)
            }
            setActiveTab(tab)
          }
        }}
        onAddSpot={handleAddSpotClick}
      />
    </div>
  )
}

function FavoriteSpots({ onSpotClick }: { onSpotClick: (spot: Spot) => void }) {
  const { t } = useTranslation()
  const { auth } = useAuth()
  const [favorites, setFavorites] = useState<Spot[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!auth.token) return
    fetch(`${API_URL}/spots/favorites/me`, {
      headers: { Authorization: `Bearer ${auth.token}` },
    })
      .then((r) => r.json())
      .then((d) => {
        setFavorites(d.items || [])
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }, [auth.token])

  if (loading) return <p className="text-gray-500">{t("loading")}</p>
  if (favorites.length === 0) return <p className="text-gray-500">{t("favorites.empty")}</p>

  return (
    <div className="space-y-3">
      {favorites.map((spot) => (
        <button
          key={spot.id}
          onClick={() => onSpotClick(spot)}
          className="w-full text-left bg-white dark:bg-slate-800 rounded-xl p-4 shadow-sm border border-gray-100 dark:border-slate-700 active:scale-[0.98] transition-transform"
        >
          <p className="font-semibold text-gray-900 dark:text-white">{spot.name}</p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {spot.rating_avg !== null && (
              <span className="mr-2">★ {spot.rating_avg}</span>
            )}
            {spot.category}
          </p>
        </button>
      ))}
    </div>
  )
}

function MySpots({ onSpotClick }: { onSpotClick: (spot: Spot) => void }) {
  const { t } = useTranslation()
  const { auth } = useAuth()
  const [mySpots, setMySpots] = useState<Spot[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!auth.token) return
    fetch(`${API_URL}/spots/me`, {
      headers: { Authorization: `Bearer ${auth.token}` },
    })
      .then((r) => r.json())
      .then((d) => {
        setMySpots(d.items || [])
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }, [auth.token])

  if (loading) return <p className="text-gray-500">{t("loading")}</p>
  if (mySpots.length === 0) return <p className="text-gray-500">{t("profile.mySpotsEmpty")}</p>

  return (
    <div className="space-y-3">
      <h3 className="font-semibold text-gray-900 dark:text-white">{t("profile.mySpots")}</h3>
      {mySpots.map((spot) => (
        <button
          key={spot.id}
          onClick={() => onSpotClick(spot)}
          className="w-full text-left bg-white dark:bg-slate-800 rounded-xl p-4 shadow-sm border border-gray-100 dark:border-slate-700 active:scale-[0.98] transition-transform"
        >
          <p className="font-semibold text-gray-900 dark:text-white">{spot.name}</p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {spot.rating_avg !== null && (
              <span className="mr-2">★ {spot.rating_avg}</span>
            )}
            {spot.category}
          </p>
        </button>
      ))}
    </div>
  )
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ThemeProvider>
  )
}

export default App
